import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AuthorizeNetEventListener, addAuthorizeNetListener, removeAuthorizeNetListener } from '../../../utils/authorizeNetCommunicator';
import AddCardIcon from '../SvgIcons/AddCardIcon';
import BankIcon from '../SvgIcons/BankIcon';

interface AuthorizeNetIframeProps {
  token: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onCancel?: () => void;
  formAction?: string;
  title?: string;
  paymentType?: 'Card' | 'Bank';
}

const AuthorizeNetIframe: React.FC<AuthorizeNetIframeProps> = ({ 
  token, 
  open, 
  onClose, 
  onSuccess, 
  onCancel, 
  formAction,
  title = "Add Payment Method",
  paymentType = "Card"
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState<number>(600);

  // Submit the form to the iframe when open/token changes
  useEffect(() => {
    if (open && token && formRef.current) {
      setIsLoading(true);
      setIframeLoaded(false);
      if (formAction) {
        formRef.current.action = formAction;
      } else {
        formRef.current.action = 'https://test.authorize.net/customer/addPayment';
      }
      formRef.current.target = 'add_payment';
      formRef.current.submit();
      if (iframeRef.current) {
        iframeRef.current.removeAttribute('hidden');
      }
    }
  }, [open, token, formAction]);

  // Handle iframe load event
  const handleIframeLoad = () => {
    setIframeLoaded(true);
    setIsLoading(false);
  };

  // Use communicator for event handling
  useEffect(() => {
    const listener: AuthorizeNetEventListener = (event) => {
      console.log(event);

      switch (event.action) {
        case 'successfulSave':
          if (onSuccess) onSuccess();
          onClose();
          break;
        case 'cancel':
          if (onCancel) onCancel();
          onClose();
          break;
        case 'resizeWindow':
          if (event.height) {
            const newHeight = Number(event.height);
            if (!isNaN(newHeight)) setIframeHeight(newHeight);
          }
          if (iframeRef.current && event.width) {
            iframeRef.current.style.width = event.width + 'px';
          }
          break;
        case 'transactResponse':
          if (iframeRef.current) {
            iframeRef.current.style.display = 'none';
          }
          break;
        case 'error':
          // Optionally handle error
          break;
        default:
          break;
      }
    };
    addAuthorizeNetListener(listener);
    return () => removeAuthorizeNetListener(listener);
  }, [onClose, onSuccess, onCancel]);

  if (!open) return null;

  const PaymentTypeIcon = () => {
    if (paymentType === 'Bank') {
      return <BankIcon width={24} height={24} color="#4edf6d" />;
    }
    return <AddCardIcon />;
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: { xs: "100%", sm: "calc(100% - 40px)" },
        maxWidth: "800px",
        margin: { xs: "10px 0", sm: "10px 20px" },
        backgroundColor: "#141414",
        borderRadius: "24px",
        border: "1px solid #FFFFFF1A",
        overflow: "hidden",
        boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.4)",
        animation: "slideIn 0.3s ease-out",
        "@keyframes slideIn": {
          "0%": {
            opacity: 0,
            transform: "scale(0.9) translateY(-20px)",
          },
          "100%": {
            opacity: 1,
            transform: "scale(1) translateY(0)",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 30px",
          backgroundColor: "#141414",
          borderBottom: "1px solid #FFFFFF1A",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              backgroundColor: "#4edf6d1A",
              border: "1px solid #4edf6d33",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": {
                  boxShadow: "0 0 0 0 rgba(78, 223, 109, 0.4)",
                },
                "70%": {
                  boxShadow: "0 0 0 10px rgba(78, 223, 109, 0)",
                },
                "100%": {
                  boxShadow: "0 0 0 0 rgba(78, 223, 109, 0)",
                },
              },
            }}
          >
            <PaymentTypeIcon />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: "white",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#a3a3a3",
                fontSize: "14px",
                marginTop: "2px",
              }}
            >
              {paymentType === 'Bank' ? 'Add your bank account details' : 'Add your credit or debit card'}
            </Typography>
          </Box>
        </Box>
        
        <IconButton
          onClick={onClose}
          sx={{
            color: "#a3a3a3",
            backgroundColor: "transparent",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            border: "1px solid #FFFFFF1A",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#FFFFFF0A",
              color: "white",
              borderColor: "#FFFFFF33",
              transform: "scale(1.1)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: "20px" }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          padding: "0",
          backgroundColor: "#141414",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Loading state */}
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 20px",
              textAlign: "center",
              maxWidth: "400px",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#141414",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "64px",
                height: "64px",
                borderRadius: "16px",
                backgroundColor: "#4edf6d1A",
                border: "1px solid #4edf6d33",
                marginBottom: "20px",
              }}
            >
              <PaymentTypeIcon />
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: "white",
                fontWeight: 600,
                marginBottom: "8px",
              }}
            >
              Loading Payment Form
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#a3a3a3",
                lineHeight: 1.5,
                marginBottom: "20px",
              }}
            >
              {paymentType === 'Bank' 
                ? 'Preparing secure bank account form...'
                : 'Preparing secure payment card form...'
              }
            </Typography>
            
            {/* Loading spinner */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <CircularProgress 
                size={24} 
                sx={{ color: "#4edf6d" }} 
              />
              <Typography
                variant="body1"
                sx={{
                  color: "#4edf6d",
                  fontWeight: 600,
                  fontSize: "16px",
                }}
              >
                Loading secure form...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Iframe Container */}
        <Box
          sx={{
            width: "100%",
            height: `${iframeHeight}px`,
            position: "relative",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            overflow: "hidden",
            border: "1px solid #FFFFFF1A",
            backgroundColor: "white",
            opacity: iframeLoaded ? 1 : 0,
            transition: "opacity 0.3s ease, height 0.3s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <iframe
            ref={iframeRef}
            id="add_payment"
            name="add_payment"
            width="100%"
            height={iframeHeight}
            frameBorder={0}
            scrolling="no"
            hidden={!open}
            onLoad={handleIframeLoad}
            style={{
              border: "none",
              display: "block",
              backgroundColor: "white",
              width: "100%",
              height: "100%",
            }}
            allowFullScreen
            title="Authorize.Net Payment Form"
          />
        </Box>
      </Box>

      {/* Hidden form for submission */}
      <form
        ref={formRef}
        id="send_token"
        method="post"
        style={{ display: "none" }}
      >
        <input type="hidden" name="token" value={token} />
      </form>
    </Box>
  );
};

export default AuthorizeNetIframe; 